import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/solid";
import {
  ArrowRightIcon,
  ClipboardIcon,
  LightningBoltIcon,
} from "@radix-ui/react-icons";
import classNames from "classnames";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { useRouter } from "next/router";
import { Fragment, useEffect, useLayoutEffect, useState } from "react";
import { SocialItem } from "~/components/footer";
import { useWindowEventListener } from "../../hooks/listeners";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { captureEvent } from "../../lib/captureEvent";
import { useModals } from "../modals/container";
import { Logo } from "../nav";
import { navigationItems } from "./constants";
import { useSideNavigation } from "./side-navigation.container";

export const EliteCard = () => {
  return (
    <div className="w-full flex overflow-hidden flex-col text-neutral-300 mt-auto flex-shrink-0 p-4 bg-neutral-800 rounded-lg my-2">
      <div className="relative z-[1]">
        <h1 className="text-base lg:text-xl mb-2">
          Upgrade your account to <b className="text-white">Elite+</b>
        </h1>
        <p className="text-sm lg:text-base">
          Get unlimited access to all Ladder Teams and save workouts.
        </p>
      </div>
      <img
        src="/assets/elite-plus-card.png"
        className="w-[60%] lg:w-full scale-150 transform translate-x-16 lg:translate-x-8 -translate-y-4 lg:-translate-y-8"
      />
      <button className="flex center justify-between">
        <span className="text-base lg:text-xl text-volt font-bold">
          Get Elite+
        </span>
        <div className="text-black bg-volt rounded-full p-2">
          <ArrowRightIcon className="w-5 h-5" />
        </div>
      </button>
    </div>
  );
};

export const SideNavigationContent = ({
  showAuth,
  showUpgradeCard,
  showLogo,
  activeTeamID,
  showFooter = true,
}) => {
  const { pathname, ...props } = useRouter();
  const { setAppLockedPromptOpen } = useModals();
  const { sidebarMinimized } = useSideNavigation();
  const { currentUser } = useCurrentUser();

  return (
    <>
      <div className="flex-1 pb-4 overflow-y-auto">
        <div className="flex py-[1.15rem] lg:hidden items-center flex-shrink-0 px-4 border-b border-neutral-800">
          <Link href="/app">
            <a className="center">
              <Logo />
            </a>
          </Link>
        </div>
        <nav aria-label="Sidebar" className="mt-5">
          <div className="px-2 space-y-1">
            {navigationItems.map((item) => {
              const current = item.current(pathname);
              return (
                <Link href={item.href} key={item.name}>
                  <motion.a
                    onClick={(e) => {
                      captureEvent(item.eventDetails);
                      if (
                        item.unavailable ||
                        (item.authenticated && !currentUser?.id)
                      ) {
                        e.preventDefault();
                        setAppLockedPromptOpen(true);
                      }
                    }}
                    className={classNames(
                      "hover:bg-neutral-700 hover:text-white group cursor-pointer flex space-x-4 items-center px-2 py-2 border-2 text-base font-medium rounded-md",
                      {
                        "lg:justify-center": sidebarMinimized,
                        "mx-auto": !sidebarMinimized,
                        "text-white border-transparent": current,
                        "border-transparent text-gray-400": !current,
                      }
                    )}
                    aria-current={current ? "page" : ""}
                  >
                    <item.icon
                      className={classNames("h-6 w-6 text-inherit")}
                      aria-hidden="true"
                    />
                    {!sidebarMinimized ? (
                      <span className="lg:flex hidden">{item.name}</span>
                    ) : null}
                    <span className="lg:hidden flex">{item.name}</span>
                  </motion.a>
                </Link>
              );
            })}
            <hr className="border-neutral-700" />
            <motion.a
              onClick={(e) => {
                captureEvent({
                  category: "General Navigation",
                  action: "Feedback Clicked",
                });
              }}
              href="https://airtable.com/shr6wdtvTqExmKmRE"
              target="_blank"
              rel="noreferrer"
              className={classNames(
                "hover:bg-neutral-700 border-transparent text-gray-400 hover:text-white group cursor-pointer flex space-x-4 items-center px-2 py-2 border-2 text-base font-medium rounded-md",
                {
                  "lg:justify-center": sidebarMinimized,
                  "mx-auto": !sidebarMinimized,
                }
              )}
            >
              <ClipboardIcon
                className={classNames("h-6 w-6 text-inherit")}
                aria-hidden="true"
              />
              {!sidebarMinimized ? (
                <span className="lg:flex hidden">BETA Feedback</span>
              ) : null}
              <span className="lg:hidden flex">BETA Feedback</span>
            </motion.a>
          </div>
          {currentUser?.id && showUpgradeCard ? (
            <motion.div>
              <AnimatePresence exitBeforeEnter>
                <motion.div
                  key={sidebarMinimized ? "LightningBoltIcon" : "EliteCard"}
                  transition={{ duration: 0.1 }}
                  initial={{ opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className={clsx({
                    "hidden lg:block p-2 min-w-[207px]": !sidebarMinimized,
                    "px-2 py-4 center hidden lg:flex": sidebarMinimized,
                  })}
                >
                  {sidebarMinimized ? (
                    <LightningBoltIcon className="w-6 h-6 text-lemon" />
                  ) : (
                    <EliteCard />
                  )}
                </motion.div>
              </AnimatePresence>

              <motion.div className="block lg:hidden p-2">
                <EliteCard />
              </motion.div>
            </motion.div>
          ) : null}
        </nav>
      </div>
      <div>
        {showFooter && !sidebarMinimized ? (
          <div className="flex items-start mb-4 space-x-4 px-4 transition-opacity opacity-80 hover:opacity-100">
            <SocialItem
              {...{
                url: "tiktok.com/@joinladder",
                imageName: "tiktok",
                invert: true,
                showName: false,
              }}
            />
            <SocialItem
              {...{
                url: "instagram.com/joinladder",
                imageName: "instagram",
                showName: false,
              }}
            />
            <SocialItem
              {...{
                url: "facebook.com/joinladder",
                imageName: "facebook",
                showName: false,
              }}
            />
            <SocialItem
              {...{
                url: "support@joinladder.com",
                urlPrefix: "mailto:",
                imageName: "mail",
                showName: false,
              }}
            />
          </div>
        ) : null}
      </div>
    </>
  );
};

const useIsomorphicEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

const topNavOffsetHeight = () =>
  document.getElementById("top-navigation")?.offsetHeight || 0;

export const SideNavigationDesktop = (props = {}) => {
  const [sideNavTop, setSideNavTop] = useState(0);

  useIsomorphicEffect(() => {
    setTimeout(() => setSideNavTop(topNavOffsetHeight), 500);
  }, []);

  useWindowEventListener("resize", () => setSideNavTop(topNavOffsetHeight), []);

  const { sidebarMinimized } = useSideNavigation();

  return (
    <div className="hidden lg:flex">
      <motion.div
        style={{
          top: sideNavTop,
          height: `calc(100vh - ${sideNavTop}px)`,
        }}
        animate={{ width: !sidebarMinimized ? 241 : 70, display: "flex" }}
        className={clsx(
          `sticky hidden lg:flex inset-y-0 bg-neutral-900 border-r border-neutral-800 w-full flex-col justify-between max-w-[14rem]`,
          {
            "min-w-[241px]": !sidebarMinimized,
            "min-w-[70px]": sidebarMinimized,
          }
        )}
      >
        <SideNavigationContent {...props} />
      </motion.div>
    </div>
  );
};

export const SideNavigation = ({ showAuth }) => {
  const { sidebarOpen, setSidebarOpen } = useSideNavigation();

  return (
    <Transition.Root show={sidebarOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 flex lg:hidden"
        onClose={setSidebarOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="fixed inset-0 bg-opacity-75 bg-neutral-600 backdrop-blur-sm" />
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="transition ease-in-out duration-300 transform"
          enterFrom="-translate-x-full"
          enterTo="translate-x-0"
          leave="transition ease-in-out duration-300 transform"
          leaveFrom="translate-x-0"
          leaveTo="-translate-x-full"
        >
          <motion.div
            layout
            className="relative flex flex-col flex-1 w-full max-w-xs bg-neutral-900 focus:outline-none"
          >
            <Transition.Child
              as={Fragment}
              enter="ease-in-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in-out duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed top-0 right-2">
                <button
                  type="button"
                  className="flex items-center justify-center w-10 h-10 ml-1 rounded-full focus:outline-none"
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="sr-only">Close sidebar</span>
                  <XIcon className="w-6 h-6 text-white" aria-hidden="true" />
                </button>
              </div>
            </Transition.Child>
            <SideNavigationContent showAuth={showAuth} />
          </motion.div>
        </Transition.Child>
        <div className="flex-shrink-0 w-14" aria-hidden="true">
          {/* Force sidebar to shrink to fit close icon */}
        </div>
      </Dialog>
    </Transition.Root>
  );
};
